/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import clsx from 'clsx'
import {UpsellImage} from 'components/UpsellImage'

interface PackageUpsellSelectableRowProps {
    title: string
    descriptionHtml?: string | null
    imageSrc?: string | null
    leadingLabel?: string | null
    meta?: React.ReactNode
    minBadge?: React.ReactNode
    maxBadge?: React.ReactNode
    price: React.ReactNode
    statusBadge?: React.ReactNode
    selected?: boolean
    isCompact?: boolean
    onClick: () => void
}

const PackageUpsellSelectableRow = ({
    title,
    descriptionHtml,
    imageSrc,
    leadingLabel,
    meta,
    minBadge,
    maxBadge,
    price,
    statusBadge,
    selected = false,
    isCompact = false,
    onClick,
}: PackageUpsellSelectableRowProps) => {
    return (
        <button
            className={clsx(
                'UpsellPriceListSimpleRow group grid w-full grid-cols-1 gap-4 px-5 py-5 text-left transition sm:grid-cols-[1fr_auto]',
                selected
                    ? 'bg-green-200/40 dark:bg-green-900/10'
                    : 'hover:bg-blue-50/60 dark:hover:bg-blue-900/10'
            )}
            type="button"
            onClick={onClick}
        >
            <div className="flex items-start gap-3 min-w-0">
                {imageSrc && (
                    <div className="shrink-0 size-12 overflow-hidden rounded-xl bg-slate-100 dark:bg-slate-800">
                        <UpsellImage alt={title} className="w-full h-full object-cover" src={imageSrc} />
                    </div>
                )}
                <div className="min-w-0">
                    {leadingLabel && (
                        <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-0.5">
                            {leadingLabel}
                        </p>
                    )}
                    <h3
                        className={clsx(
                            'font-semibold text-slate-950 dark:text-slate-100 group-hover:text-blue-700 dark:group-hover:text-blue-400 leading-snug',
                            isCompact ? 'text-sm' : 'text-base'
                        )}
                    >
                        {title}
                    </h3>
                    {descriptionHtml && (
                        <p
                            className="mt-1 text-sm leading-5 text-slate-500 dark:text-slate-400 line-clamp-2"
                            dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                        />
                    )}
                    {meta && <div className="mt-1 flex flex-wrap gap-1.5">{meta}</div>}
                </div>
            </div>

            <div className="grid items-center gap-3 grid-cols-3 sm:items-end sm:justify-center shrink-0 min-w-sm">
                {minBadge}
                {maxBadge}
                <div className="text-sm font-bold text-slate-950 dark:text-slate-100 col-start-3 text-end">
                    {price}
                </div>
                {statusBadge}
            </div>
        </button>
    )
}

export default PackageUpsellSelectableRow